import { Button, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import {
  getListMyTeamsQueryKey,
  useCreateMyTeam,
} from "../../api/generated/endpoints/teams/teams";
import { TeamCreate, teamCreateNameMax } from "../../api/generated/zod";
import { refreshAccessScope } from "../../api/session";
import { AdminPanelHint } from "../../platform/AdminPanelHint";
import { ErrorAlert } from "../../platform/ErrorAlert";

const NAME_PROBLEM = `Give the team a name of up to ${teamCreateNameMax} characters.`;

/** New team on its own route: a team made opens its home. */
export function NewTeamScreen() {
  const navigate = useNavigate();
  return (
    <NewTeam onCreated={(teamId) => navigate({ to: "/teams/$teamId", params: { teamId } })} />
  );
}

/**
 * A team the user owns (`POST /v1/me/teams`), wherever it is drawn (Classic's route, or Pro's,
 * spec ui-perf/ui-switch-classic-simple-pro PR e5b). Open only while the API's self-serve flag is
 * on; otherwise the API answers TEAM_CREATION_CLOSED, which shows here, since the SPA cannot know
 * the flag (D1). Owning one team is the limit (TEAM_LIMIT_REACHED). Staff in no team also see the
 * way to the Admin Panel, where their teams are set up (spec ui-perf/staff-no-team-admin-panel).
 */
export function NewTeam({ onCreated }: { onCreated: (teamId: string) => Promise<void> }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [invalid, setInvalid] = useState(false);
  const create = useCreateMyTeam({
    mutation: {
      onSuccess: async (team) => {
        // The scope first: the team route's gate reads it.
        await Promise.all([
          refreshAccessScope(queryClient),
          queryClient.invalidateQueries({ queryKey: getListMyTeamsQueryKey() }),
        ]);
        await onCreated(team.id);
      },
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = TeamCreate.safeParse({ name: name.trim() });
    setInvalid(!parsed.success);
    if (parsed.success) create.mutate({ data: parsed.data });
  };
  return (
    <Stack gap="lg" maw={480}>
      <Title order={1}>Create a team</Title>
      <Text c="var(--cai-color-text-muted)">
        You own the team you create and can invite others to it. While Created.ai is invite-only,
        the Created.ai team sets up new teams: ask us for one.
      </Text>
      <AdminPanelHint />
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          <TextInput
            label="Team name"
            maxLength={teamCreateNameMax}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            error={invalid ? NAME_PROBLEM : undefined}
          />
          {create.isError ? <ErrorAlert error={create.error} /> : null}
          <Group>
            <Button type="submit" loading={create.isPending}>
              Create team
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
}
